/**
 * ScheduleDraftTimetableSection — ScheduleDraft Timetable 区块装配层。
 *
 * 本组件只负责连接工作台 Context、Tab、指标、可编辑 Grid、只读 Table 与弹窗
 * 编辑器。行模型、列定义和 Grid 提交状态已下沉到 timetable 子模块，避免
 * Timetable 再次成为数据转换与交互逻辑混杂的单体组件。
 *
 * 设计文档 → docs/20_workspace_ux/21_workspace_layout.md
 * 骨架关联 → components/editor/timetable/timetableRowModel.js (行模型), components/editor/timetable/timetableColumns.js (列定义), components/editor/timetable/useTimetableGridEditing.js (Grid 编辑状态)
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CloseCircleFilled, ImportOutlined, InfoCircleOutlined, PlusOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Checkbox, Empty, Input, message, Popover, Space, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useQueryClient } from "react-query";
import { useTimeZone } from "../../../../../../contexts/TimeZoneContext";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { referenceDataQueryKeys } from "../../../hooks/queryKeys";
import useBatchAirportChange from "../../../hooks/useBatchAirportChange";
import useScheduleDraftEditorWorkspace from "../../../hooks/useScheduleDraftEditorWorkspace";
import {
    BATCH_AUTO_CALCULATE_OPTIONS,
    buildBatchPreviewFromChangedFlights,
    buildPreviewFromAutoCalculate,
    formatAutoCalculatePreviewTime,
    getAutoCalculateAffectedFlightId,
    getAutoCalculateStopReasonLabel,
} from "../../../lib/autoCalculatePreviewPipeline";
import { AUTO_CALCULATE_FLIGHTS, BATCH_SHIFT } from "../../../lib/editCommand";
import { getModifiedStatus, getSelectedLocalFlights } from "../../../lib/flightDataUtils";
import { getIsoDay } from "../../../lib/flightTimingUtils";
import { buildRouteReferenceData, getRouteById, mergeReferenceData, normalizeAirportCode } from "../../../lib/routeUtils";
import { buildTrackDeleteAction, getDraftFlights } from "../../../lib/trackUtils";
import { scheduleDraftReferenceDataService } from "../../../services/scheduleDraftReferenceDataService";
import TimetableBatchActionBar from "./TimetableBatchActionBar";
import TimetableColumnSettingsPopover from "./TimetableColumnSettingsPopover";
import TimetableEditableGrid from "./TimetableEditableGrid";
import TimetableFlightEditModal from "./TimetableFlightEditModal";
import TimetableMetrics from "./TimetableMetrics";
import TimetableReadonlyTable from "./TimetableReadonlyTable";
import TimetableTabs from "./TimetableTabs";
import { buildEditableColumns, buildReadonlyColumns } from "./timetableColumns";
import { filterTimetableRows, getTimetableQuickFilterTokens } from "./timetableQuickFilter";
import useFlightNumberSuggestions from "./useFlightNumberSuggestions";
import useTimetableGridEditing from "./useTimetableGridEditing";

const { Text } = Typography;
const EMPTY_COLUMN_WIDTHS = {};
const EMPTY_COLUMN_ORDER = [];
const EMPTY_HIDDEN_COLUMN_KEYS = [];
const EMPTY_SELECTED_CELLS = new Set();

// index.js/App.jsx do not initialize dayjs plugins yet; keep module-level UTC registration.
dayjs.extend(utc);

const BATCH_SHIFT_FIELDS = ["std", "sta", "flight_date", "day_of_week", "change_status", "change_source"];
const AIRPORT_CHANGE_FIELDS = ["departure_airport", "arrival_airport"];
const REFERENCE_ROUTE_STALE_TIME = 5 * 60 * 1000;
const DELETED_CHANGE_STATUS = "DELETED";
const DEFAULT_AUTO_CALCULATE_OPTIONS = BATCH_AUTO_CALCULATE_OPTIONS;

function attachTrackDeleteActions(items = [], draft, deleteOptions) {
    return (items || []).map((item) => {
        const deleteAction = buildTrackDeleteAction(draft, item?.track, deleteOptions);
        if (!deleteAction) {
            return item;
        }
        const existingActions = Array.isArray(item.trackActions) ? item.trackActions : item.trackAction ? [item.trackAction] : [];
        const otherActions = existingActions.filter((action) => action?.type !== deleteAction.type);
        return {
            ...item,
            trackActions: [deleteAction, ...otherActions],
        };
    });
}

const AUTO_CALCULATE_OPTION_CONFIGS = [
    { key: "consider_min_ground_time", label: "考虑最小过站", description: "关闭后仅不主动为过站调整时间，H3 分析和发布校验仍照常显示。" },
    { key: "consider_curfew", label: "考虑宵禁", description: "开启后预览会避开机场宵禁窗口。" },
    { key: "consider_transport_demand", label: "考虑运输需求", description: "开启后预览会参考运输需求的时间窗口。" },
    { key: "minimum_impact_mode", label: "最小影响模式", description: "开启后当后续航班追平原计划时停止继续向后计算。" },
];

const TOOLBAR_AUTO_OPERATION_CONFIGS = {
    cascade: {
        type: AUTO_CALCULATE_FLIGHTS,
        label: "全部重算",
        previewTitle: "全部重算预览",
        noChangeMessage: "全部重算未产生变更",
        errorMessage: "全部重算失败",
    },
};

function isColumnWidths(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value) && Object.values(value).every((width) => Number.isFinite(width) && width > 0);
}

function isStringArray(value) {
    return Array.isArray(value) && value.every((item) => typeof item === "string" && item.length > 0);
}

function applyColumnWidths(columns = [], columnWidths = EMPTY_COLUMN_WIDTHS) {
    return columns.map((column) => {
        const width = columnWidths[column.key];
        if (!Number.isFinite(width) || width <= 0) {
            return column;
        }

        return {
            ...column,
            width,
        };
    });
}

function applyColumnOrder(columns = [], columnOrder = []) {
    const columnsByKey = new Map(columns.map((column) => [column.key, column]));
    const orderedColumns = [];
    (Array.isArray(columnOrder) ? columnOrder : []).forEach((key) => {
        const column = columnsByKey.get(key);
        if (column && !orderedColumns.some((item) => item.key === key)) {
            orderedColumns.push(column);
        }
    });

    columns.forEach((column) => {
        if (!orderedColumns.some((item) => item.key === column.key)) {
            orderedColumns.push(column);
        }
    });

    return orderedColumns;
}

function filterHiddenColumns(columns = [], hiddenColumnKeys = []) {
    const hiddenColumnKeySet = new Set(Array.isArray(hiddenColumnKeys) ? hiddenColumnKeys : []);
    return columns.filter((column) => !hiddenColumnKeySet.has(column.key));
}

function getFlightRows(rows = []) {
    return rows.filter((row) => row?._rowType === "flight" && row?.rawFlight?.id && !isDeletedFlightRow(row));
}

function isDeletedFlightRow(row) {
    return String(row?.rawFlight?.change_status || row?.changeStatus || "").toUpperCase() === DELETED_CHANGE_STATUS;
}

function getFirstFlight(rows = []) {
    return getFlightRows(rows)[0]?.rawFlight || null;
}

function buildShiftedFlight(flight, offsetMinutes) {
    const std = dayjs.utc(flight?.std);
    const sta = dayjs.utc(flight?.sta);
    if (!std.isValid() || !sta.isValid()) {
        throw new Error(`航班 ${flight?.flight_number || flight?.id || ""} 缺少有效 STD/STA，无法偏移`.trim());
    }

    const shiftedStd = std.add(offsetMinutes, "minute");
    const shiftedSta = sta.add(offsetMinutes, "minute");
    return {
        ...flight,
        std: shiftedStd.toISOString(),
        sta: shiftedSta.toISOString(),
        flight_date: shiftedStd.format("YYYY-MM-DD"),
        day_of_week: getIsoDay(shiftedStd),
        change_status: getModifiedStatus(flight),
        change_source: "USER",
    };
}

function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object || {}, key);
}

function hasRouteReferenceDataForLeg(referenceData = {}, departureAirport, arrivalAirport) {
    const routesByLeg = referenceData?.routesByLeg;
    if (!routesByLeg || !departureAirport || !arrivalAirport) {
        return false;
    }

    const routeKeys = [
        `${departureAirport}:${arrivalAirport}`,
        `${departureAirport}-${arrivalAirport}`,
        `${departureAirport}_${arrivalAirport}`,
    ];

    if (routesByLeg instanceof Map) {
        return routeKeys.some((key) => routesByLeg.has(key));
    }

    return routeKeys.some((key) => hasOwn(routesByLeg, key));
}

function hasRouteReferenceDataForId(referenceData = {}, routeId) {
    if (routeId === undefined || routeId === null || routeId === "") {
        return true;
    }
    return Boolean(getRouteById(referenceData, routeId));
}

function getPayloadOrFlightValue(payload, flight, field) {
    return hasOwn(payload, field) ? payload[field] : flight?.[field];
}

function getRouteTimingReferenceRouteId(command, currentFlight) {
    const payload = command?.payload || {};
    const routeId = getPayloadOrFlightValue(payload, currentFlight, "flight_route_id");
    return routeId === undefined || routeId === null || routeId === "" ? null : routeId;
}

function commandNeedsRouteTimingReference(command, currentFlight) {
    const payload = command?.payload || {};
    if (!currentFlight) {
        return false;
    }
    if (!hasOwn(payload, "std") && !hasOwn(payload, "sta") && !hasOwn(payload, "flight_route_id")) {
        return false;
    }
    return getRouteTimingReferenceRouteId(command, currentFlight) !== null;
}

function buildSingleRouteReferenceData(route) {
    if (!route || route.id === undefined || route.id === null) {
        return null;
    }

    return {
        routes: [route],
        routesById: {
            [route.id]: route,
        },
    };
}

function formatPreviewRouteValue(value) {
    if (!value) {
        return "未绑定";
    }

    if (typeof value === "object") {
        return value.route_name || value.routeName || value.name || value.id || "航路";
    }

    return String(value);
}

function buildPreviewRouteChangeDetails(changeSet, localFlightsMap) {
    const routeFields = new Set(["flight_route_name", "route_snapshot", "flight_route_id"]);
    const changesByFlightId = new Map();

    (changeSet?.changes || []).forEach((change) => {
        const flightId = change.flightId ?? change.flight_id;
        if (!flightId || !routeFields.has(change.field)) {
            return;
        }

        const flightIdStr = String(flightId);
        if (!changesByFlightId.has(flightIdStr)) {
            changesByFlightId.set(flightIdStr, {});
        }
        changesByFlightId.get(flightIdStr)[change.field] = change;
    });

    return Array.from(changesByFlightId.entries()).map(([flightId, changes]) => {
        const routeChange = changes.route_snapshot || changes.flight_route_name || changes.flight_route_id;
        const flight = localFlightsMap.get(flightId);

        return {
            flightId,
            flightLabel: flight?.flight_number || flightId,
            before: formatPreviewRouteValue(routeChange?.before),
            after: formatPreviewRouteValue(routeChange?.after),
        };
    });
}

function hasAirportPayloadChange(payload = {}, flight = {}) {
    return AIRPORT_CHANGE_FIELDS.some((field) => hasOwn(payload, field) && normalizeAirportCode(payload[field]) !== normalizeAirportCode(flight?.[field]));
}

const quickFilterHelpContent = (
    <div className="schedule-draft-timetable-filter-help">
        <Text strong>使用技巧</Text>
        <div className="schedule-draft-timetable-filter-help-row"><Text code>B-1234</Text><Text type="secondary">筛选特定飞机</Text></div>
        <div className="schedule-draft-timetable-filter-help-row"><Text code>PEK CAN</Text><Text type="secondary">同时涉及 PEK 和 CAN 的航班</Text></div>
        <div className="schedule-draft-timetable-filter-help-row"><Text code>B-1234 801</Text><Text type="secondary">筛选特定机号下的航班号</Text></div>
        <div className="schedule-draft-timetable-filter-help-row"><Text code>801</Text><Text type="secondary">航班号包含 801</Text></div>
        <div className="schedule-draft-timetable-filter-help-row"><Text code>北京</Text><Text type="secondary">涉及“北京”机场的航班</Text></div>
    </div>
);

function CalculationSettingsContent({ options, onOptionChange }) {
    return (
        <div className="schedule-draft-timetable-calculation-settings" onMouseDown={(event) => event.stopPropagation()}>
            <Text strong>自动计算预览选项</Text>
            <Text type="secondary">仅影响后续生成的自动计算预览，不写入 Draft，也不会改变 dirty/apply/save 边界。</Text>
            <div className="schedule-draft-timetable-calculation-option-list">
                {AUTO_CALCULATE_OPTION_CONFIGS.map((item) => (
                    <div key={item.key} className="schedule-draft-timetable-calculation-option">
                        <Checkbox checked={Boolean(options[item.key])} onChange={(event) => onOptionChange(item.key, event.target.checked)}>
                            {item.label}
                        </Checkbox>
                        <Text type="secondary">{item.description}</Text>
                    </div>
                ))}
            </div>
            <div className="schedule-draft-timetable-calculation-scope">
                <Space wrap size={[6, 6]}>
                    <Tag color="blue">范围</Tag>
                    <Tag>从起始航班开始向后计算</Tag>
                </Space>
                <Text type="secondary">当前接口通过 start_flight_id 指定起始锚点，计算该锚点及之后的同机号连续页面链；行内计算从本行开始，工具栏从当前轨首个航班开始。</Text>
            </div>
        </div>
    );
}

function countFlightRows(rows = []) {
    return rows.filter((row) => row?._rowType === "flight").length;
}

function TimetableQuickFilter({ value, onChange, active, totalFlightCount, filteredFlightCount }) {
    return (
        <div className="schedule-draft-timetable-quick-filter">
            <Input
                className="schedule-draft-timetable-quick-filter-input"
                value={value}
                prefix={<SearchOutlined />}
                placeholder="搜索机号、机场、航班性质、航班号或机场中文名，如 B-1234 PEK 801"
                onChange={(event) => onChange(event.target.value)}
                suffix={(
                    <Space size={2} onMouseDown={(event) => event.preventDefault()}>
                        <Popover trigger="click" placement="bottomRight" content={quickFilterHelpContent}>
                            <Button type="text" size="small" aria-label="表格搜索帮助" icon={<InfoCircleOutlined />} />
                        </Popover>
                        {value && (
                            <Button type="text" size="small" aria-label="清除表格搜索" icon={<CloseCircleFilled />} onClick={() => onChange("")} />
                        )}
                    </Space>
                )}
            />
            {active && (
                <Text type="secondary" className="schedule-draft-timetable-quick-filter-count">
                    已筛选 {filteredFlightCount} / {totalFlightCount} 个航班
                </Text>
            )}
        </div>
    );
}

function TimetableToolsRow({
    value,
    onChange,
    active,
    totalFlightCount,
    filteredFlightCount,
    autoCalculateOptions,
    onAutoCalculateOptionChange,
    addFlightDisabledReason,
    onAddFlight,
    importFlightsDisabledReason,
    onImportFlights,
    showAutoCalculate,
    autoCalculateDisabledReason,
    hasEditableFlights,
    toolbarAutoOperationDisabled,
    previewBuildingOperation,
    onToolbarAutoOperation,
    columnSettings,
}) {
    return (
        <div className="schedule-draft-timetable-tools-row">
            <TimetableQuickFilter
                value={value}
                onChange={onChange}
                active={active}
                totalFlightCount={totalFlightCount}
                filteredFlightCount={filteredFlightCount}
            />
            <Space wrap size={[8, 8]} className="schedule-draft-timetable-tools-actions">
                <Tooltip title={addFlightDisabledReason || "在当前编辑轨新增航班"}>
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => onAddFlight?.()} disabled={Boolean(addFlightDisabledReason)}>
                        新增航班
                    </Button>
                </Tooltip>
                <Tooltip title={importFlightsDisabledReason || "从来源 / 表格导入航班到当前编辑轨"}>
                    <Button size="small" icon={<ImportOutlined />} onClick={() => onImportFlights?.()} disabled={Boolean(importFlightsDisabledReason)}>
                        导入航班
                    </Button>
                </Tooltip>
                {showAutoCalculate ? (
                    <Tooltip title={autoCalculateDisabledReason || (!hasEditableFlights ? "当前轨没有可重算的航班" : "从当前轨首个航班开始全部重算")}>
                        <Button size="small" disabled={toolbarAutoOperationDisabled} loading={previewBuildingOperation === "cascade"} onClick={() => onToolbarAutoOperation?.("cascade")}>全部重算</Button>
                    </Tooltip>
                ) : null}
                {columnSettings || null}
                <Popover
                    trigger="click"
                    placement="bottomRight"
                    content={<CalculationSettingsContent options={autoCalculateOptions} onOptionChange={onAutoCalculateOptionChange} />}
                >
                    <Button size="small" icon={<SettingOutlined />}>计算设置</Button>
                </Popover>
            </Space>
        </div>
    );
}

/**
 * 渲染工作台下方的 Timetable 观察与编辑区。
 * @returns {React.ReactElement} Timetable 区块。
 */
const ScheduleDraftTimetableSection = ({ onTrackAction } = {}) => {
    const { airportsMap } = useTimeZone();
    const queryClient = useQueryClient();
    const [editingFlight, setEditingFlight] = useState(null);
    const [autoCalculatePreview, setAutoCalculatePreview] = useState(null);
    const [autoCalculatingFlightId, setAutoCalculatingFlightId] = useState(null);
    const [previewBuildingOperation, setPreviewBuildingOperation] = useState(null);
    const [committingAutoCalculate, setCommittingAutoCalculate] = useState(false);
    const [activeActionRowId, setActiveActionRowId] = useState(null);
    const [rowContextMenu, setRowContextMenu] = useState(null);
    const [timetableReferenceData, setTimetableReferenceData] = useState({ routes: [], routesById: {} });
    const [autoCalculateOptions, setAutoCalculateOptions] = useState(DEFAULT_AUTO_CALCULATE_OPTIONS);
    const {
        draftId,
        data: { draft, timetableModel, localEditState, localCompareMatchState, refetchDraft, refetchAnalysis, airportConstraintsMap, isAgentWorking },
        ui: {
            timetableTab,
            setTimetableTab,
            referenceTrackItems = [],
            setSelectedItemId,
            timetableQuickFilterQuery,
            setTimetableQuickFilterQuery,
            selectedCells,
            setSelectedCells,
            clearSelectedCells,
            hoveredCell,
            setHoveredCell,
            currentEditingTrackId,
            openAddFlightDialog,
            openSourceTrackDialog,
        },
        mutations: { handleDeleteFlight, deletingFlight },
        editPipeline,
        permissions: { canUpdateFlight, canDeleteFlight, canDeleteTrack },
    } = useScheduleDraftEditorWorkspace();

    const isNewTab = ["draft", "editing"].includes(timetableModel.activeSourceTrack?.kind);
    const quickFilterTokens = useMemo(() => getTimetableQuickFilterTokens(timetableQuickFilterQuery), [timetableQuickFilterQuery]);
    const isQuickFilterActive = quickFilterTokens.length > 0;
    const filteredTimetableRows = useMemo(() => filterTimetableRows(timetableModel.rows, timetableQuickFilterQuery, airportsMap), [airportsMap, timetableModel.rows, timetableQuickFilterQuery]);
    const totalFlightCount = useMemo(() => countFlightRows(timetableModel.rows), [timetableModel.rows]);
    const filteredFlightCount = useMemo(() => countFlightRows(filteredTimetableRows), [filteredTimetableRows]);
    const hasEditableFlights = useMemo(() => getFlightRows(timetableModel.rows).length > 0, [timetableModel.rows]);
    const [columnWidths, setColumnWidths] = useLocalStorage("timetableGrid.columnWidths", EMPTY_COLUMN_WIDTHS, { scope: "prefs", validate: isColumnWidths });
    const [columnOrder, setColumnOrder] = useLocalStorage("timetableGrid.columnOrder", EMPTY_COLUMN_ORDER, { scope: "prefs", validate: isStringArray });
    const [hiddenColumnKeys, setHiddenColumnKeys] = useLocalStorage("timetableGrid.hiddenColumns", EMPTY_HIDDEN_COLUMN_KEYS, { scope: "prefs", validate: isStringArray });
    const flightNumberSuggestions = useFlightNumberSuggestions({ draftId, rows: isNewTab ? timetableModel.rows : [] });
    const timetableSourceTrackOptions = useMemo(
        () => attachTrackDeleteActions(timetableModel.sourceTrackOptions, draft, { canDeleteTrack, isAgentWorking }),
        [canDeleteTrack, draft, isAgentWorking, timetableModel.sourceTrackOptions],
    );
    const timetableReferenceTrackItems = useMemo(
        () => attachTrackDeleteActions(referenceTrackItems, draft, { canDeleteTrack, isAgentWorking }),
        [canDeleteTrack, draft, isAgentWorking, referenceTrackItems],
    );
    const loadAirportChangeReferenceData = useCallback(
        async (command, currentFlight) => {
            const payload = command?.payload || {};
            if (!currentFlight || !hasAirportPayloadChange(payload, currentFlight)) {
                return null;
            }

            const departureAirport = normalizeAirportCode(getPayloadOrFlightValue(payload, currentFlight, "departure_airport"));
            const arrivalAirport = normalizeAirportCode(getPayloadOrFlightValue(payload, currentFlight, "arrival_airport"));
            if (!departureAirport || !arrivalAirport) {
                return null;
            }

            if (hasRouteReferenceDataForLeg(command?.meta?.referenceData, departureAirport, arrivalAirport)) {
                return null;
            }

            const routeOptions = await queryClient.fetchQuery(
                referenceDataQueryKeys.flightRoutes(departureAirport, arrivalAirport),
                () => scheduleDraftReferenceDataService.getRoutesByLeg(departureAirport, arrivalAirport),
                { staleTime: REFERENCE_ROUTE_STALE_TIME },
            );

            return buildRouteReferenceData(routeOptions);
        },
        [queryClient],
    );
    const loadRouteTimingReferenceData = useCallback(
        async (command, currentFlight, baseReferenceData = {}) => {
            if (!commandNeedsRouteTimingReference(command, currentFlight)) {
                return null;
            }

            const routeId = getRouteTimingReferenceRouteId(command, currentFlight);
            const commandReferenceData = mergeReferenceData(baseReferenceData, command?.meta?.referenceData || {});
            if (hasRouteReferenceDataForId(commandReferenceData, routeId)) {
                return null;
            }

            const route = await queryClient.fetchQuery(
                referenceDataQueryKeys.flightRoute(routeId),
                () => scheduleDraftReferenceDataService.getRouteById(routeId),
                { staleTime: REFERENCE_ROUTE_STALE_TIME },
            );
            if (!route) {
                throw new Error(`未找到航路 ${routeId} 的引用数据`);
            }

            return buildSingleRouteReferenceData(route);
        },
        [queryClient],
    );
    const executeCommandWithSuggestionInvalidation = useCallback(
        async (command) => {
            const beforeFlightsById = new Map();
            let beforeFlight = null;
            if (command?.flightId) {
                const flightId = String(command.flightId);
                beforeFlight = localEditState.localFlightsMap.get(flightId);
                beforeFlightsById.set(flightId, beforeFlight);
            }

            let commandToExecute = command;
            try {
                const additionalReferenceData = [];
                const routeReferenceData = await loadRouteTimingReferenceData(command, beforeFlight);
                if (routeReferenceData) {
                    additionalReferenceData.push(routeReferenceData);
                }

                const airportReferenceData = await loadAirportChangeReferenceData(command, beforeFlight);
                if (airportReferenceData) {
                    additionalReferenceData.push(airportReferenceData);
                }

                if (additionalReferenceData.length) {
                    const mergedCommandReferenceData = mergeReferenceData(command.meta?.referenceData || {}, ...additionalReferenceData);
                    setTimetableReferenceData((current) => mergeReferenceData(current, ...additionalReferenceData));
                    commandToExecute = {
                        ...command,
                        meta: {
                            ...(command.meta || {}),
                            referenceData: mergedCommandReferenceData,
                        },
                    };
                }
            } catch (error) {
                message.error(error.response?.data?.detail || error.message || "航路数据加载失败，修改未应用");
                return {
                    status: "blocked",
                    command,
                    issues: [
                        {
                            scope: "flight",
                            entityId: command?.flightId,
                            severity: "error",
                            code: "ROUTE_REFERENCE_DATA_LOAD_FAILED",
                            message: "航路数据加载失败，修改未应用",
                            blocking: true,
                            source: "reference-data",
                        },
                    ],
                };
            }

            const result = await editPipeline.executeCommand(commandToExecute);
            if (result?.status === "success") {
                flightNumberSuggestions.invalidateChangedCombos(result.changeSet, beforeFlightsById);
            }
            return result;
        },
        [editPipeline, flightNumberSuggestions, loadAirportChangeReferenceData, loadRouteTimingReferenceData, localEditState.localFlightsMap],
    );
    const loadReferenceDataForGridEdit = useCallback(
        async ({ row, payload, referenceData } = {}) => {
            const currentFlight = row?.rawFlight || null;
            const command = {
                payload: payload || {},
                meta: {
                    referenceData: referenceData || {},
                },
            };
            try {
                const routeReferenceData = await loadRouteTimingReferenceData(command, currentFlight, referenceData || {});
                if (!routeReferenceData) {
                    return referenceData || {};
                }

                setTimetableReferenceData((current) => mergeReferenceData(current, routeReferenceData));
                return mergeReferenceData(referenceData || {}, routeReferenceData);
            } catch (error) {
                message.error(error.response?.data?.detail || error.message || "航路数据加载失败，修改未应用");
                throw error;
            }
        },
        [loadRouteTimingReferenceData],
    );
    const gridEditing = useTimetableGridEditing({
        draftId,
        rows: filteredTimetableRows,
        canUpdateFlight,
        executeCommand: executeCommandWithSuggestionInvalidation,
        setSelectedItemId,
        selectedCells,
        setSelectedCells,
        clearSelectedCells,
        hoveredCell,
        setHoveredCell,
        referenceData: timetableReferenceData,
        loadReferenceDataForEdit: loadReferenceDataForGridEdit,
    });
    const currentEditingTrack = (draft?.tracks || []).find((track) => String(track.id) === String(currentEditingTrackId)) || null;
    const defaultCopyStartDate = currentEditingTrack?.period_start || draft?.period_start;

    const handleAutoCalculateOptionChange = useCallback((key, checked) => {
        setAutoCalculateOptions((current) => ({
            ...current,
            [key]: Boolean(checked),
        }));
    }, []);

    useEffect(() => {
        if (!rowContextMenu) {
            return undefined;
        }

        const closeContextMenu = () => setRowContextMenu(null);
        window.addEventListener("click", closeContextMenu);
        window.addEventListener("scroll", closeContextMenu, true);
        return () => {
            window.removeEventListener("click", closeContextMenu);
            window.removeEventListener("scroll", closeContextMenu, true);
        };
    }, [rowContextMenu]);

    const handleRefreshLocalState = useCallback(async () => {
        if (!refetchDraft) {
            return;
        }
        if (localEditState?.dirtyState?.isDirty || localCompareMatchState?.dirtyState?.isDirty) {
            message.warning("请先保存或放弃本地航班/匹配修改后再刷新表格");
            return;
        }

        const freshDraft = await refetchDraft();
        if (freshDraft?.data) {
            localEditState.initFromServer(getDraftFlights(freshDraft.data));
        }
        await refetchAnalysis?.();
    }, [localCompareMatchState?.dirtyState?.isDirty, localEditState, refetchAnalysis, refetchDraft]);

    const handleCancelAutoCalculatePreview = useCallback(() => {
        setAutoCalculatePreview(null);
        localEditState.clearPreview();
    }, [localEditState]);

    const baseAutoCalculateDisabledReason = useMemo(() => {
        if (!canUpdateFlight) {
            return "缺少 schedule:update 权限";
        }
        if (autoCalculatingFlightId || previewBuildingOperation) {
            return "自动计算请求处理中";
        }
        if (autoCalculatePreview) {
            return "请先确认或取消当前自动计算预览";
        }
        return null;
    }, [autoCalculatePreview, autoCalculatingFlightId, canUpdateFlight, previewBuildingOperation]);

    const {
        handleBatchChangeAirport,
        handleBatchSwitchLine,
        isProcessing: batchAirportChangeProcessing,
    } = useBatchAirportChange({
        draftId,
        localFlightsMap: localEditState.localFlightsMap,
        disabledReason: baseAutoCalculateDisabledReason,
        airportConstraintsMap,
        autoCalculateOptions,
        clearPreview: localEditState.clearPreview,
        setPendingPreview: localEditState.setPendingPreview,
        setAutoCalculatePreview,
    });

    const autoCalculateDisabledReason = useMemo(() => {
        if (batchAirportChangeProcessing) {
            return "自动计算请求处理中";
        }
        return baseAutoCalculateDisabledReason;
    }, [baseAutoCalculateDisabledReason, batchAirportChangeProcessing]);

    const requestAutoCalculatePreview = useCallback(
        async ({ startFlight, options, type = AUTO_CALCULATE_FLIGHTS, label, previewTitle = "自动计算预览", noChangeMessage = "自动计算未产生变更", errorMessage = "自动计算失败" }) => {
            if (!draftId || !startFlight?.id || autoCalculateDisabledReason) {
                if (autoCalculateDisabledReason) {
                    message.warning(autoCalculateDisabledReason);
                }
                return null;
            }

            try {
                localEditState.clearPreview();
                const preview = await buildPreviewFromAutoCalculate({
                    draftId,
                    localFlightsMap: localEditState.localFlightsMap,
                    startFlight,
                    options,
                    type,
                    label,
                    airportConstraintsMap,
                });

                if (!preview.changeSet.changes.length) {
                    setAutoCalculatePreview(null);
                    message.info(`${noChangeMessage}：${getAutoCalculateStopReasonLabel(preview.response?.stop_reason)}`);
                    return null;
                }

                const nextPreview = {
                    ...preview,
                    previewTitle,
                };
                setAutoCalculatePreview(nextPreview);
                localEditState.setPendingPreview(preview.changeSet);
                message.info(`${previewTitle}已生成；点击“确认应用”后 Timeline 才会更新。`);
                return nextPreview;
            } catch (error) {
                message.error(error.response?.data?.detail || error.message || errorMessage);
                return null;
            }
        },
        [airportConstraintsMap, autoCalculateDisabledReason, draftId, localEditState],
    );

    const requestBatchPreview = useCallback(
        async ({ changedFlightsById, fields, type, label, previewTitle, noChangeMessage, errorMessage }) => {
            if (!draftId || autoCalculateDisabledReason) {
                if (autoCalculateDisabledReason) {
                    message.warning(autoCalculateDisabledReason);
                }
                return null;
            }

            try {
                const preview = await buildBatchPreviewFromChangedFlights({
                    draftId,
                    localFlightsMap: localEditState.localFlightsMap,
                    changedFlightsById,
                    fields,
                    type,
                    label,
                    options: autoCalculateOptions,
                    airportConstraintsMap,
                    beforeCalculate: localEditState.clearPreview,
                });

                if (!preview) {
                    message.warning("未找到可计算的航班链");
                    return null;
                }

                if (!preview.changeSet.changes.length) {
                    setAutoCalculatePreview(null);
                    message.info(noChangeMessage || `${label}未产生变更`);
                    return null;
                }

                const nextPreview = {
                    ...preview,
                    previewTitle,
                };
                setAutoCalculatePreview(nextPreview);
                localEditState.setPendingPreview(preview.changeSet);
                message.info(`${previewTitle || label || "批量预览"}已生成；点击“确认应用”后 Timeline 才会更新。`);
                return nextPreview;
            } catch (error) {
                message.error(error.response?.data?.detail || error.message || errorMessage || `${label}失败`);
                return null;
            }
        },
        [airportConstraintsMap, autoCalculateDisabledReason, autoCalculateOptions, draftId, localEditState],
    );

    const handleBatchShiftTime = useCallback(
        async ({ flightIds, offsetMinutes }) => {
            if (autoCalculateDisabledReason) {
                message.warning(autoCalculateDisabledReason);
                return null;
            }

            const changedFlightsById = new Map();
            getSelectedLocalFlights(localEditState.localFlightsMap, flightIds).forEach((flight) => {
                const shiftedFlight = buildShiftedFlight(flight, offsetMinutes);
                changedFlightsById.set(String(shiftedFlight.id), shiftedFlight);
            });
            if (!changedFlightsById.size) {
                message.warning("请选择需要时间偏移的航班");
                return null;
            }

            setPreviewBuildingOperation("batch-shift");
            try {
                return await requestBatchPreview({
                    changedFlightsById,
                    fields: BATCH_SHIFT_FIELDS,
                    type: BATCH_SHIFT,
                    label: `时间偏移 ${offsetMinutes > 0 ? "+" : ""}${offsetMinutes}min`,
                    previewTitle: "时间偏移预览",
                    noChangeMessage: "时间偏移未产生变更",
                    errorMessage: "时间偏移失败",
                });
            } finally {
                setPreviewBuildingOperation(null);
            }
        },
        [autoCalculateDisabledReason, localEditState.localFlightsMap, requestBatchPreview],
    );

    const handleRequestAutoCalculate = useCallback(
        async (row) => {
            const startFlight = row?.rawFlight;
            if (!startFlight?.id || autoCalculateDisabledReason) {
                if (autoCalculateDisabledReason) {
                    message.warning(autoCalculateDisabledReason);
                }
                return;
            }

            setAutoCalculatingFlightId(startFlight.id);
            try {
                await requestAutoCalculatePreview({
                    startFlight,
                    options: autoCalculateOptions,
                    type: AUTO_CALCULATE_FLIGHTS,
                    previewTitle: "自动计算预览",
                    noChangeMessage: "自动计算未产生变更",
                    errorMessage: "自动计算失败",
                });
            } finally {
                setAutoCalculatingFlightId(null);
            }
        },
        [autoCalculateDisabledReason, autoCalculateOptions, requestAutoCalculatePreview],
    );

    const handleRequestToolbarAutoOperation = useCallback(
        async (operationKey) => {
            const operationConfig = TOOLBAR_AUTO_OPERATION_CONFIGS[operationKey];
            if (!operationConfig) {
                return;
            }
            if (autoCalculateDisabledReason) {
                message.warning(autoCalculateDisabledReason);
                return;
            }

            setPreviewBuildingOperation(operationKey);
            try {
                const startFlight = getFirstFlight(timetableModel.rows);
                if (!startFlight?.id) {
                    message.warning("当前轨没有可重算的航班");
                    return;
                }

                await requestAutoCalculatePreview({
                    startFlight,
                    options: autoCalculateOptions,
                    type: operationConfig.type,
                    label: operationConfig.label,
                    previewTitle: operationConfig.previewTitle,
                    noChangeMessage: operationConfig.noChangeMessage,
                    errorMessage: operationConfig.errorMessage,
                });
            } catch (error) {
                message.error(error.response?.data?.detail || error.message || operationConfig.errorMessage);
            } finally {
                setPreviewBuildingOperation(null);
            }
        },
        [autoCalculateDisabledReason, autoCalculateOptions, requestAutoCalculatePreview, timetableModel.rows],
    );

    const handleOpenAddFlightAtRow = useCallback(
        (row, mode) => {
            const flight = row?.rawFlight;
            if (!canUpdateFlight || !flight?.id || isDeletedFlightRow(row)) {
                return;
            }

            openAddFlightDialog({
                mode,
                anchorFlightId: flight.id,
                anchorFlight: flight,
                trackId: currentEditingTrackId,
            });
            setRowContextMenu(null);
        },
        [canUpdateFlight, currentEditingTrackId, openAddFlightDialog],
    );

    const handleOpenImportFlights = useCallback(() => {
        if (!currentEditingTrackId) {
            message.warning("缺少当前编辑轨，无法导入航班");
            return;
        }

        openSourceTrackDialog?.({
            lockTargetTrack: true,
            initialValues: {
                mode: "IMPORT",
                targetTrackId: currentEditingTrackId,
            },
        });
    }, [currentEditingTrackId, openSourceTrackDialog]);

    const handleRowContextMenu = useCallback(
        ({ row }, event) => {
            if (!canUpdateFlight || row?._rowType !== "flight" || !row?.rawFlight?.id || isDeletedFlightRow(row)) {
                return;
            }

            setRowContextMenu({
                x: event.clientX,
                y: event.clientY,
                row,
            });
        },
        [canUpdateFlight],
    );

    const handleConfirmAutoCalculate = useCallback(async () => {
        if (!autoCalculatePreview) {
            return;
        }

        setCommittingAutoCalculate(true);
        try {
            const result = await executeCommandWithSuggestionInvalidation({
                type: autoCalculatePreview.changeSet?.type || autoCalculatePreview.type || AUTO_CALCULATE_FLIGHTS,
                draftId,
                payload: {
                    changeSet: autoCalculatePreview.changeSet,
                },
                meta: {
                    source: "auto-calculate-preview",
                    response: autoCalculatePreview.response,
                    startFlightId: autoCalculatePreview.startFlight?.id,
                    operationType: autoCalculatePreview.changeSet?.type || autoCalculatePreview.type,
                },
            });

            if (result?.status !== "success") {
                message.warning(`${autoCalculatePreview.label || "自动计算"}未应用`);
                return;
            }

            const affectedCount = new Set((autoCalculatePreview.changeSet?.changes || []).map((change) => String(change.flightId ?? change.flight_id))).size;
            localEditState.clearPreview();
            setAutoCalculatePreview(null);
            message.success(`已应用${autoCalculatePreview.label || "自动计算"}到本地编辑，影响 ${affectedCount} 个航班`);
        } catch (error) {
            message.error(error.response?.data?.detail || error.message || "应用自动计算失败");
        } finally {
            setCommittingAutoCalculate(false);
        }
    }, [autoCalculatePreview, draftId, executeCommandWithSuggestionInvalidation, localEditState]);

    const editableColumns = useMemo(
        () => buildEditableColumns({
            canUpdateFlight,
            canDeleteFlight,
            deletingFlight,
            onOpenEdit: setEditingFlight,
            onDeleteFlight: handleDeleteFlight,
            onAutoCalculate: handleRequestAutoCalculate,
            autoCalculatingFlightId,
            autoCalculateDisabledReason,
            getFlightNumberSuggestion: flightNumberSuggestions.getSuggestion,
            loadFlightNumberSuggestion: flightNumberSuggestions.loadSuggestion,
            activeRouteCellKey: gridEditing.activeRouteCellKey,
            onOpenRouteSearch: gridEditing.openRouteSearch,
            onCloseRouteSearch: gridEditing.closeRouteSearch,
            onSelectRoute: gridEditing.handleRouteSelect,
            activeActionRowId,
            onActiveActionRowChange: setActiveActionRowId,
            onAddFlightAtRow: handleOpenAddFlightAtRow,
        }),
        [activeActionRowId, autoCalculateDisabledReason, autoCalculatingFlightId, canDeleteFlight, canUpdateFlight, deletingFlight, flightNumberSuggestions.getSuggestion, flightNumberSuggestions.loadSuggestion, gridEditing.activeRouteCellKey, gridEditing.closeRouteSearch, gridEditing.handleRouteSelect, gridEditing.openRouteSearch, handleDeleteFlight, handleOpenAddFlightAtRow, handleRequestAutoCalculate],
    );

    const columns = useMemo(
        () => {
            if (!isNewTab) {
                return buildReadonlyColumns({ useEffectiveTime: timetableModel.usesEffectiveTime });
            }

            return applyColumnWidths(
                filterHiddenColumns(
                    applyColumnOrder(editableColumns, columnOrder),
                    hiddenColumnKeys,
                ),
                columnWidths,
            );
        },
        [columnOrder, columnWidths, editableColumns, hiddenColumnKeys, isNewTab, timetableModel.usesEffectiveTime],
    );

    const handleColumnResize = useCallback(
        (columnKey, width) => {
            if (!columnKey || !Number.isFinite(width) || width <= 0) {
                return;
            }

            setColumnWidths((current) => ({
                ...current,
                [columnKey]: width,
            }));
        },
        [setColumnWidths],
    );

    const autoCalculateAffectedFlights = autoCalculatePreview?.response?.affected_flights || [];
    const autoCalculateAdjustmentCount = autoCalculateAffectedFlights.reduce((total, affectedFlight) => total + (affectedFlight.adjustments?.length || 0), 0);
    const autoCalculateSampleFlights = autoCalculateAffectedFlights.slice(0, 3);
    const autoCalculateRouteChangeDetails = buildPreviewRouteChangeDetails(autoCalculatePreview?.changeSet, localEditState.localFlightsMap);
    const autoCalculateRouteChangeSamples = autoCalculateRouteChangeDetails.slice(0, 3);
    const autoCalculateChangedFlightCount = new Set((autoCalculatePreview?.changeSet?.changes || []).map((change) => String(change.flightId ?? change.flight_id))).size;
    const toolbarAutoOperationDisabled = Boolean(autoCalculateDisabledReason) || !hasEditableFlights;
    const addFlightDisabledReason = !canUpdateFlight
        ? "缺少 schedule:update 权限"
        : !isNewTab
            ? "当前表格源为只读参考"
            : !currentEditingTrackId
                ? "缺少当前编辑轨"
                : null;
    const importFlightsDisabledReason = !canUpdateFlight
        ? "缺少 schedule:update 权限"
        : !isNewTab
            ? "当前表格源为只读参考"
            : !currentEditingTrackId
                ? "缺少当前编辑轨"
                : null;
    const renderTimetableBody = () => {
        if (timetableModel.mode !== "table") {
            return (
                <div className="schedule-draft-timetable-placeholder">
                    <Empty description={timetableModel.emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            );
        }

        if (!timetableModel.rows.length) {
            return <Empty description={timetableModel.emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        }

        if (!filteredTimetableRows.length) {
            return <Empty description="没有匹配复合搜索条件的航班。" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        }

        if (!isNewTab) {
            return <TimetableReadonlyTable columns={columns} rows={filteredTimetableRows} />;
        }

        return (
            <TimetableEditableGrid
                columns={columns}
                rows={gridEditing.gridRows}
                selectedCells={selectedCells}
                selectedCellRange={gridEditing.selectedCellRange}
                selectedCellRef={gridEditing.selectedCellRef}
                gridRef={gridEditing.gridRef}
                canUpdateFlight={canUpdateFlight}
                onRowsChange={gridEditing.handleGridRowsChange}
                onSelectRow={gridEditing.handleSelectRow}
                onCellMouseDown={gridEditing.handleCellMouseDown}
                onCellMouseMove={gridEditing.handleCellMouseMove}
                onCellMouseUp={gridEditing.handleCellMouseUp}
                onCellNavigation={gridEditing.handleCellNavigation}
                onColumnHeaderClick={gridEditing.handleColumnHeaderClick}
                onClearCellSelection={gridEditing.clearCellSelection}
                onCopySelectedCells={gridEditing.copySelectedCellRange}
                onColumnResize={handleColumnResize}
                onRowContextMenu={handleRowContextMenu}
                rowClass={gridEditing.rowClass}
            />
        );
    };

    const autoCalculatePreviewActionRow = autoCalculatePreview ? (
        <Alert
            className="schedule-draft-auto-calculate-preview"
            type="info"
            showIcon
            message={(
                <Space wrap size={[8, 4]}>
                    <Text strong>{autoCalculatePreview.previewTitle || "自动计算预览"}</Text>
                    <Tag color="blue">影响 {autoCalculateChangedFlightCount} 个航班</Tag>
                    <Tag>{getAutoCalculateStopReasonLabel(autoCalculatePreview.response?.stop_reason)}</Tag>
                    {autoCalculateAdjustmentCount > 0 && <Tag color="gold">约束调整 {autoCalculateAdjustmentCount}</Tag>}
                    {autoCalculateRouteChangeDetails.length > 0 && <Tag color="purple">航路变更 {autoCalculateRouteChangeDetails.length}</Tag>}
                </Space>
            )}
            description={(
                <div className="schedule-draft-auto-calculate-preview-body">
                    <Space wrap size={[8, 6]}>
                        {autoCalculateSampleFlights.map((affectedFlight) => {
                            const flightId = String(getAutoCalculateAffectedFlightId(affectedFlight));
                            const flight = localEditState.localFlightsMap.get(flightId);
                            return (
                                <Tag key={flightId} color="processing">
                                    {flight?.flight_number || flightId}: {formatAutoCalculatePreviewTime(affectedFlight.original_std)} → {formatAutoCalculatePreviewTime(affectedFlight.new_std)}
                                </Tag>
                            );
                        })}
                        {autoCalculateAffectedFlights.length > autoCalculateSampleFlights.length && <Tag>+{autoCalculateAffectedFlights.length - autoCalculateSampleFlights.length}</Tag>}
                    </Space>
                    {autoCalculateRouteChangeSamples.length > 0 && (
                        <Space wrap size={[8, 6]}>
                            <Text type="secondary">航路</Text>
                            {autoCalculateRouteChangeSamples.map((routeChange) => (
                                <Tag key={routeChange.flightId} color="purple">
                                    {routeChange.flightLabel}: {routeChange.before} → {routeChange.after}
                                </Tag>
                            ))}
                            {autoCalculateRouteChangeDetails.length > autoCalculateRouteChangeSamples.length && <Tag>+{autoCalculateRouteChangeDetails.length - autoCalculateRouteChangeSamples.length}</Tag>}
                        </Space>
                    )}
                    <Text type="secondary">表格中已标出预览差异；确认后进入本地编辑与 Undo 栈，最终由现有保存按钮统一写入后端。</Text>
                </div>
            )}
            action={(
                <Space>
                    <Button type="primary" size="small" loading={committingAutoCalculate} onClick={handleConfirmAutoCalculate}>
                        确认应用
                    </Button>
                    <Button size="small" disabled={committingAutoCalculate} onClick={handleCancelAutoCalculatePreview}>
                        取消
                    </Button>
                </Space>
            )}
        />
    ) : null;

    return (
        <Card className="schedule-draft-main-card schedule-draft-timetable-card" bordered={false}>
            <TimetableTabs
                activeKey={timetableTab}
                referenceTrackItems={timetableReferenceTrackItems}
                sourceTrackOptions={timetableSourceTrackOptions}
                onChange={setTimetableTab}
                onTrackAction={onTrackAction}
            />
            <TimetableToolsRow
                value={timetableQuickFilterQuery}
                onChange={setTimetableQuickFilterQuery}
                active={isQuickFilterActive}
                totalFlightCount={totalFlightCount}
                filteredFlightCount={filteredFlightCount}
                autoCalculateOptions={autoCalculateOptions}
                onAutoCalculateOptionChange={handleAutoCalculateOptionChange}
                addFlightDisabledReason={addFlightDisabledReason}
                onAddFlight={openAddFlightDialog}
                importFlightsDisabledReason={importFlightsDisabledReason}
                onImportFlights={handleOpenImportFlights}
                showAutoCalculate={isNewTab}
                autoCalculateDisabledReason={autoCalculateDisabledReason}
                hasEditableFlights={hasEditableFlights}
                toolbarAutoOperationDisabled={toolbarAutoOperationDisabled}
                previewBuildingOperation={previewBuildingOperation}
                onToolbarAutoOperation={handleRequestToolbarAutoOperation}
                columnSettings={isNewTab ? (
                    <TimetableColumnSettingsPopover
                        columns={editableColumns}
                        columnOrder={columnOrder}
                        hiddenColumnKeys={hiddenColumnKeys}
                        onColumnOrderChange={setColumnOrder}
                        onHiddenColumnKeysChange={setHiddenColumnKeys}
                    />
                ) : null}
            />
            {(isNewTab || autoCalculatePreviewActionRow) && (
                <TimetableBatchActionBar
                    draftId={draftId}
                    rows={isNewTab ? gridEditing.gridRows : []}
                    selectedCells={isNewTab ? selectedCells : EMPTY_SELECTED_CELLS}
                    currentEditingTrackId={currentEditingTrackId}
                    defaultCopyStartDate={defaultCopyStartDate}
                    canUpdateFlight={isNewTab && canUpdateFlight}
                    canDeleteFlight={isNewTab && canDeleteFlight}
                    executeCommand={executeCommandWithSuggestionInvalidation}
                    onRefreshLocalState={handleRefreshLocalState}
                    onClearSelection={gridEditing.clearCellSelection}
                    onBatchShiftTime={handleBatchShiftTime}
                    onBatchChangeAirport={handleBatchChangeAirport}
                    onBatchSwitchLine={handleBatchSwitchLine}
                    batchPreviewDisabledReason={autoCalculatePreview || previewBuildingOperation || autoCalculatingFlightId || batchAirportChangeProcessing ? autoCalculateDisabledReason : null}
                    previewActionRow={autoCalculatePreviewActionRow}
                />
            )}
            {renderTimetableBody()}
            <TimetableMetrics metrics={timetableModel.metrics} />
            {rowContextMenu && (
                <div
                    className="schedule-draft-timetable-row-context-menu"
                    style={{ left: rowContextMenu.x, top: rowContextMenu.y }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <button type="button" onClick={() => handleOpenAddFlightAtRow(rowContextMenu.row, "before")}>在上方插入</button>
                    <button type="button" onClick={() => handleOpenAddFlightAtRow(rowContextMenu.row, "after")}>在下方插入</button>
                </div>
            )}

            <TimetableFlightEditModal
                editingFlight={editingFlight}
                draftId={draftId}
                onClose={() => setEditingFlight(null)}
                executeCommand={executeCommandWithSuggestionInvalidation}
                canUpdateFlight={canUpdateFlight}
            />
        </Card>
    );
};

export default ScheduleDraftTimetableSection;
